import { cn } from './cn';

export interface IconProps {
  /** Material Symbols Outlined glyph name, e.g. "whatshot". */
  name: string;
  /** Pixel size. Optical sizing is matched to it automatically. */
  size?: number;
  /** Filled variant. */
  filled?: boolean;
  /** Stroke weight — 400 default, 600 for small dense UI. */
  weight?: 300 | 400 | 500 | 600 | 700;
  /** Accessible name. Omit for decorative icons sitting next to text. */
  label?: string;
  className?: string;
}

/**
 * The single icon source across the HOTTR ecosystem: Material Symbols Outlined.
 *
 * Canon forbids Lucide / Heroicons / Feather / emoji-as-UI. One icon set is a
 * large part of why sister surfaces read as related — mixed sets are the
 * fastest way to make a product look assembled rather than designed.
 *
 * Requires the Material Symbols stylesheet in the host `index.html` and the
 * `.material-symbols-outlined` base rule (both shipped by the sync script).
 */
export const Icon = ({
  name,
  size = 22,
  filled = false,
  weight = 400,
  label,
  className,
}: IconProps) => (
  <span
    aria-hidden={label ? undefined : true}
    aria-label={label}
    role={label ? 'img' : undefined}
    translate="no"
    className={cn('material-symbols-outlined select-none', className)}
    style={{
      fontSize: size,
      // Lock the box to the glyph size so icons never shift a row's height.
      width: size,
      height: size,
      lineHeight: `${size}px`,
      fontVariationSettings: `'FILL' ${filled ? 1 : 0}, 'wght' ${weight}, 'GRAD' 0, 'opsz' ${size}`,
    }}
  >
    {name}
  </span>
);
